"use client";

import { useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

type Status = "idle" | "loading" | "done" | "error";

export function NewsletterSignup({ eyebrow }: { eyebrow: string }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  async function onSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setStatus(res.ok ? "done" : "error");
    } catch {
      setStatus("error");
    }
  }

  if (status === "done") {
    return (
      <p className="text-primary mt-5 flex items-center gap-2 text-sm font-medium">
        <Check className="h-4 w-4" />
        You&rsquo;re subscribed — talk soon.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-5 max-w-xs">
      <p className="text-muted-foreground text-2xs mb-2 uppercase tracking-label">
        {eyebrow}
      </p>
      <div className="focus-within:ring-ring flex items-center gap-2 rounded-sm border-b border-white/15 pb-1.5 focus-within:border-white/30 focus-within:ring-2 focus-within:ring-offset-1">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") setStatus("idle");
          }}
          placeholder="Enter your email"
          aria-label="Email address"
          className="placeholder:text-muted-foreground/70 flex-1 bg-transparent text-sm outline-none"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          aria-label="Subscribe"
          className={cn(
            "text-foreground/70 hover:text-foreground transition-colors disabled:opacity-50",
          )}
        >
          {status === "loading" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
        </button>
      </div>
      {status === "error" && (
        <p className="text-warning mt-1.5 text-xs">
          Couldn&rsquo;t subscribe. Try again in a moment.
        </p>
      )}
    </form>
  );
}
